/** @jsx jsx */
import {
  jsx,
  ResponsiveStyleValue,
  ThemeUIStyleObject,
  useThemeUI,
} from "theme-ui";
import React, { useRef, useState, PropsWithChildren } from "react";
import { em } from "polished";
import LazyLoad from "react-lazyload";
import opacities from "@styles/design-tokens/07.opacities/opacities";
import transitions from "@styles/design-tokens/09.transitions/transitions";
import { millisToSeconds } from "@styles/utils/millisToSeconds";
import { Image, ImageProps } from "../Image";
import { serializeQueryParameters } from "../../../utils/serialize-query-parameters";
import { ImageField } from "../../../../carlsberg-headless/types/fields/Image";
import { Theme } from "@styles/theme";
import { motion } from "framer-motion";
import { AspectRatio } from "../../AspectRatio/AspectRatio";
import { aspectRatioString } from "@styles/utils/aspectRatioString";
import {
  ImageProcessorParams,
  imageProcessorParamsSerializer,
} from "../../../../carlsberg-headless/image-processor/serialize-query-parameters";
import { generateImageProcessorParamsWithWidthAndHeight } from "../../../../carlsberg-headless/image-processor/generateImageProcessorParamsWithWidthAndHeight";
import objectFitImages from "object-fit-images";
import { Nullable } from "../../../types/util";

export interface ImageSource {
  media?: string;
  srcSet: string;
  srcSetWebp: string;
  urlParams: Record<string, ImageProcessorParams>;
}

export interface UmbracoImageProps
  extends Omit<ImageProps, "src" | "width" | "height"> {
  params?: ResponsiveStyleValue<ImageProcessorParams>;
  image: ImageField;
  width?: ResponsiveStyleValue<string | number>;
  height?: ResponsiveStyleValue<string | number>;
  isCover?: boolean;
  lazyLoadOffset?: number;
}

export const calculateResolutionSize = (
  value: string | number,
  resolution: number
) => (typeof value === "number" ? value * resolution : value);

export const generateImageSources = (
  image: ImageField,
  resolutions: number[],
  breakpoints: string[],
  width?: ResponsiveStyleValue<string | number>,
  height?: ResponsiveStyleValue<string | number>,
  imageProcessorParams?: ResponsiveStyleValue<ImageProcessorParams>
): ImageSource[] => {
  const mappableSources: ImageSource[] = [];

  if (!Array.isArray(width) && !Array.isArray(height)) {
    const srcSet: string[] = [];
    const srcSetWebP: string[] = [];
    // const urlParams: Record<string, string | number> = {};

    const mappableSource: ImageSource = {
      srcSet: "",
      srcSetWebp: "",
      urlParams: {},
    };

    resolutions.forEach((resolution) => {
      const mappableProps: ImageProcessorParams =
        generateImageProcessorParamsWithWidthAndHeight(
          resolution,
          0,
          width,
          height,
          image?.focalPoint,
          imageProcessorParams as ImageProcessorParams
        );

      // Create a srcSet url from query parameters - postfix with resolution x value
      srcSet.push(
        `${serializeQueryParameters(
          image.fullUrl as string,
          mappableProps,
          imageProcessorParamsSerializer
        )} ${resolution}x`
      );

      // Create a srcSet url specifically for webp images by using the format feature in Imageprocessor
      srcSetWebP.push(
        `${serializeQueryParameters(
          image.fullUrl as string,
          {
            ...mappableProps,
            format: "webp",
          },
          imageProcessorParamsSerializer
        )} ${resolution}x`
      );

      mappableSource.urlParams[resolution] = mappableProps;
    });

    mappableSource.srcSet = [...srcSet].join(",");
    mappableSource.srcSetWebp = [...srcSetWebP].join(",");

    mappableSources.push(mappableSource);

    return mappableSources;
  }

  breakpoints.forEach((breakpoint, breakpointIndex) => {
    // While we are looping over _all_ breakpoints in the theme system
    // we do not want to process a breakpoint if it was not provided
    // in props.width or props.height.
    // const breakpoints = [1,2,3]
    // const widthProps = [50,100] <-- shorter array than max breakpoints
    // const widthProps = [50,null,100] <-- skips a breakpoint
    if (
      Array.isArray(width) &&
      (width[breakpointIndex] === null || !width[breakpointIndex]) &&
      Array.isArray(height) &&
      (height[breakpointIndex] === null || !height[breakpointIndex])
    ) {
      return;
    }

    // For each breakpoint we will need a collection of resolution specific src URLs
    // URLs are built from the images orignal source and a set query parameters
    const srcSet: string[] = [];
    const srcSetWebP: string[] = [];

    const mappableSource: ImageSource = {
      // media query for <source/>
      media: "",
      srcSet: "",
      srcSetWebp: "",
      urlParams: {},
    };

    if (breakpointIndex === 0) {
      // If we are the first breakpoint, since we are working mobile-first, the initial media query is max-width
      // "from 0 up until next breakpoint - 1px"
      const maxWidth = em(parseInt(breakpoints[1]) * 16 - 1);

      mappableSource.media = `(max-width: ${maxWidth})`;
    } else if (breakpoints.length > 2 && breakpoints[breakpointIndex + 1]) {
      // If this breakpoint is followed by another breakpoint we are not at the first, nor the last
      // "from this breakpoint up until next breakpoint - 1px"
      const maxWidth = em(parseInt(breakpoints[breakpointIndex + 1]) * 16 - 1);

      mappableSource.media = `(min-width: ${breakpoint}) and (max-width: ${maxWidth})`;
    } else {
      // We are at the final breakpoint
      // "from this breakpoint and up"
      mappableSource.media = `(min-width: ${breakpoint})`;
    }

    // For this breakpoint, for each resolution...
    resolutions.forEach((resolution) => {
      const mappableProps: ImageProcessorParams =
        generateImageProcessorParamsWithWidthAndHeight(
          breakpointIndex,
          resolution,
          width,
          height,
          image?.focalPoint,
          imageProcessorParams
        );

      // Create a srcSet url from query parameters - postfix with resolution x value
      srcSet.push(
        `${serializeQueryParameters(
          image.fullUrl as string,
          mappableProps,
          imageProcessorParamsSerializer
        )} ${resolution}x`
      );

      // Create a srcSet url specifically for webp images by using the format feature in Imageprocessor
      srcSetWebP.push(
        `${serializeQueryParameters(
          image.fullUrl as string,
          {
            ...mappableProps,
            format: "webp",
          },
          imageProcessorParamsSerializer
        )} ${resolution}x`
      );

      mappableSource.urlParams[resolution] = mappableProps;
    });

    // combine srcSet urls from all resolution to one string
    mappableSource.srcSet = [...srcSet].join(",");
    mappableSource.srcSetWebp = [...srcSetWebP].join(",");

    mappableSources.push(mappableSource);
  });

  return mappableSources;
};

const handleIEObjectFit = (imageElement?: Nullable<HTMLImageElement>) => {
  if (!imageElement) return;

  const supportsObjectFit = "object-fit" in imageElement.style;

  if (supportsObjectFit) return;

  objectFitImages(imageElement, {
    watchMQ: true,
  });
};

export const UmbracoImageStyles: ThemeUIStyleObject = {};

// These are the resolutions we compute srcSet urls from - they are project specific and cannot be modified by components
export const UMBRACO_IMAGE_RESOLUTIONS = [1, 1.5, 2];

const PictureWrapper: React.FC<
  PropsWithChildren<{ isFluid: boolean; aspectRatio?: string }>
> = ({ isFluid, aspectRatio, children }) =>
  isFluid ? (
    <AspectRatio ratio={aspectRatio as string}>{children}</AspectRatio>
  ) : (
    <React.Fragment>{children}</React.Fragment>
  );
const UmbracoImage: React.FC<UmbracoImageProps> = ({
  sx,
  width,
  height,
  image,
  params,
  isCover,
  lazyLoadOffset,
  alt,
  ...props
}) => {
  const [imageIsLoaded, setImageIsLoaded] = useState(false);
  const handleSetImage = () => {
    setImageIsLoaded(true);
  };

  const themeUiContext = useThemeUI();
  const theme = themeUiContext.theme as Theme;

  const imageRef = useRef<HTMLImageElement | null>(null);

  if (!image) {
    return null;
  }

  if (image.extension === "svg") {
    return (
      <LazyLoad once offset={lazyLoadOffset ?? -150}>
        <motion.div
          initial={{ opacity: opacities[0] }}
          animate={{ opacity: opacities[imageIsLoaded ? 100 : 0] }}
          transition={{
            duration: millisToSeconds(transitions.durations["500"]),
          }}
        >
          <Image
            src={image.fullUrl as string}
            sx={{ ...UmbracoImageStyles, ...sx }}
            {...props}
            onLoad={handleSetImage}
          />
        </motion.div>
      </LazyLoad>
    );
  }

  const aspectRatio: string | undefined =
    image?.width && image?.height
      ? aspectRatioString(image.width, image.height)
      : undefined;

  const isFluid = !(!!width && !!height);

  const imageSources = generateImageSources(
    image,
    UMBRACO_IMAGE_RESOLUTIONS,
    ["0", ...(theme.breakpoints as string[])],
    width,
    height,
    params
  );

  return (
    <LazyLoad
      {...(isCover && { sx: { height: "100%" } })}
      once
      offset={lazyLoadOffset ?? -150}
    >
      <motion.div
        {...(isCover && { sx: { height: "100%" } })}
        initial={{ opacity: opacities[0] }}
        animate={{ opacity: opacities[imageIsLoaded ? 100 : 0] }}
        transition={{
          duration: millisToSeconds(transitions.durations["500"]),
        }}
      >
        <PictureWrapper
          isFluid={isCover ? false : isFluid}
          {...(isFluid && { aspectRatio })}
        >
          <picture>
            {imageSources.map(
              ({ srcSet, srcSetWebp, media }, index: number) => (
                <React.Fragment key={`${srcSet}_${index}`}>
                  <source type="image/webp" srcSet={srcSetWebp} media={media} />
                  <source srcSet={srcSet} media={media} />
                </React.Fragment>
              )
            )}

            <Image
              ref={imageRef}
              src={image.fullUrl as string}
              srcSet={imageSources[0].srcSet}
              alt={alt}
              sx={{
                ...UmbracoImageStyles,
                ...(isCover && {
                  objectFit: "cover",
                  fontFamily: "'object-fit: cover'", // IE fix
                  width: "100%",
                  height: "100%",
                }),
                ...sx,
              }}
              {...props}
              onLoad={() => {
                handleSetImage();
                handleIEObjectFit(imageRef?.current);
              }}
            />
          </picture>
        </PictureWrapper>
      </motion.div>
    </LazyLoad>
  );
};

UmbracoImage.defaultProps = {};

export { UmbracoImage };
